import type { CatalogItem, InvoicePreviewLine } from "../../api/types";
import { LineItemPicker, LineZoneSelect } from "./PaperLinePickers";
import type { LineLink } from "./PaperOpenLinesTable";

function matchHintLabel(status: string): string {
  const map: Record<string, string> = {
    MATCHED_ALIAS: "Matched (supplier SKU)",
    MATCHED_ITEM_SKU: "Matched (item SKU)",
    // G-114: name matching — the staged item this supplier's printed text
    // became, or the one catalogue item called exactly this.
    MATCHED_STAGED_NAME: "Matched (staged name)",
    MATCHED_NAME: "Matched (item name)",
  };
  return map[status] ?? status;
}

/**
 * What the badge says. A stored link the matcher cannot re-find (the app
 * linked a no-code line at read time, or a person saved a pick) is
 * "Linked"; an unsaved pick on a line the matcher found nothing for is
 * "Picked by hand".
 */
function linkedBadge(line: InvoicePreviewLine): string {
  if (line.matchHints.status !== "NEEDS_ITEM") return matchHintLabel(line.matchHints.status);
  return line.itemId ? "Linked" : "Picked by hand";
}

/**
 * G-129 — the paper's lines that have an item: the one the app created from
 * the line, the one the matcher found, or the one a person picked. Compact,
 * one row per line; the item can still be changed and the zone set.
 */
export function PaperLinkedLinesList({
  lines,
  links,
  items,
  zones,
  lineMoney,
  onPickItem,
  onPickZone,
}: {
  lines: InvoicePreviewLine[];
  links: Record<string, LineLink>;
  items: CatalogItem[];
  zones: Array<{ id: string; name: string }>;
  /** The line's money, formatted by the page (qty × unit → total, with the paper's extras). */
  lineMoney: (line: InvoicePreviewLine) => string;
  onPickItem: (lineId: string, itemId: string) => void;
  onPickZone: (lineId: string, zoneId: string) => void;
}) {
  return (
    <div className="table-scroll">
      <table className="paper-lines">
        <thead>
          <tr>
            <th>Line</th>
            <th>Printed</th>
            <th>Item</th>
            <th>Receiving zone</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const link = links[line.id] ?? { itemId: "", zoneId: "" };
            // OD-112: a code match whose item is no longer called what the
            // supplier printed — shown beside the line so a reused code is
            // noticed.
            const matchedDifferentName =
              line.matchHints.printedNameDiffers && line.matchHints.itemName
                ? line.matchHints.itemName
                : null;
            return (
              <tr key={line.id}>
                <td>
                  <div className="paper-line__head">#{line.lineIndex + 1}</div>
                  <code>{line.supplierSku ?? "—"}</code>
                </td>
                <td>
                  <div>{line.descriptionRaw}</div>
                  <div className="muted paper-line__money">{lineMoney(line)}</div>
                  {matchedDifferentName ? (
                    <div className="muted paper-line__money">
                      Matched by code to <strong>{matchedDifferentName}</strong> — the printed name
                      differs; check it is the same product.
                    </div>
                  ) : null}
                </td>
                <td>
                  <span className="badge success">{linkedBadge(line)}</span>
                  <LineItemPicker
                    lineId={line.id}
                    itemId={link.itemId}
                    items={items}
                    onPick={(itemId) => onPickItem(line.id, itemId)}
                  />
                </td>
                <td>
                  <LineZoneSelect
                    lineId={line.id}
                    zoneId={link.zoneId}
                    zones={zones}
                    onPick={(zoneId) => onPickZone(line.id, zoneId)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
